/**
 * Estado del catálogo del usuario: búsqueda, vista (cards/list), página y
 * apps ocultadas. La vista es global; las apps ocultas se persisten por
 * entra_object_id para que no se filtren entre usuarios del mismo navegador.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

export type ViewMode = 'cards' | 'list';

const VIEW_MODE_KEY = 'appsol:view-mode';
const HIDDEN_KEY = (oid: string) => `appsol:hidden:${oid}`;
export const PAGE_SIZE = 12;

function readJson<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage llena o bloqueada: no es crítico para la UX.
  }
}

export interface CatalogState {
  search: string;
  setSearch: (v: string) => void;
  category: string | null;
  setCategory: (v: string | null) => void;
  subcategory: string | null;
  setSubcategory: (v: string | null) => void;
  view: ViewMode;
  setView: (v: ViewMode) => void;
  page: number;
  setPage: (n: number) => void;
  hidden: ReadonlySet<string>;
  hideApp: (slug: string) => void;
  restoreApp: (slug: string) => void;
  restoreAll: () => void;
}

export function useCatalogState(entraObjectId: string | undefined): CatalogState {
  const [search, setSearchRaw] = useState('');
  const [category, setCategoryRaw] = useState<string | null>(null);
  const [subcategory, setSubcategoryRaw] = useState<string | null>(null);
  const [view, setViewRaw] = useState<ViewMode>(() => {
    const stored = typeof window === 'undefined' ? null : window.localStorage.getItem(VIEW_MODE_KEY);
    return stored === 'list' ? 'list' : 'cards';
  });
  const [page, setPage] = useState(1);
  const [hiddenList, setHiddenList] = useState<string[]>([]);

  useEffect(() => {
    if (!entraObjectId) {
      setHiddenList([]);
      return;
    }
    setHiddenList(readJson<string[]>(HIDDEN_KEY(entraObjectId), []));
  }, [entraObjectId]);

  const persistHidden = useCallback(
    (next: string[]) => {
      setHiddenList(next);
      if (entraObjectId) writeJson(HIDDEN_KEY(entraObjectId), next);
    },
    [entraObjectId],
  );

  const setView = useCallback((v: ViewMode) => {
    setViewRaw(v);
    if (typeof window !== 'undefined') window.localStorage.setItem(VIEW_MODE_KEY, v);
  }, []);

  const setSearch = useCallback((v: string) => {
    setSearchRaw(v);
    setPage(1);
  }, []);

  const setCategory = useCallback((v: string | null) => {
    setCategoryRaw(v);
    setSubcategoryRaw(null); // al cambiar de categoría, la subcategoría deja de aplicar
    setPage(1);
  }, []);

  const setSubcategory = useCallback((v: string | null) => {
    setSubcategoryRaw(v);
    setPage(1);
  }, []);

  const hideApp = useCallback(
    (slug: string) => {
      if (hiddenList.includes(slug)) return;
      persistHidden([...hiddenList, slug]);
    },
    [hiddenList, persistHidden],
  );

  const restoreApp = useCallback(
    (slug: string) => persistHidden(hiddenList.filter((s) => s !== slug)),
    [hiddenList, persistHidden],
  );

  const restoreAll = useCallback(() => persistHidden([]), [persistHidden]);

  const hidden = useMemo(() => new Set(hiddenList), [hiddenList]);

  return {
    search, setSearch,
    category, setCategory,
    subcategory, setSubcategory,
    view, setView,
    page, setPage,
    hidden, hideApp, restoreApp, restoreAll,
  };
}
